/**
 * Gold Crest (Texas, a separate company) on To order. It has no forecast or delivery dates, so its buying number is
 * the API's 60-days-of-use rule: 60 days at the last 30 days' pace, minus stock and open orders (monitor.ts).
 * Listed: every Gold Crest part where that number is above zero, the soonest to run out first.
 */
import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import type { MonitorRow } from "@march/shared";
import { apiGet } from "../lib/api.ts";
import { CATEGORY_LABEL, isYards } from "../lib/categories.ts";
import { downloadCsv } from "../lib/csv.ts";
import { partName } from "../lib/skuNames.ts";
import { addDays, fmt, shortDate, todayIso } from "../lib/supply.ts";
import { ExportButton, ListBar } from "./ListBar.tsx";

const TYPES = ["Frame", "Cover", "Base", "Fabric", "Trim", "ProtectiveCover"] as const;

/** Gold Crest parts with something to order, across every category. */
export function useGoldCrest(enabled = true) {
  const qs = useQueries({
    queries: TYPES.map((t) => ({
      queryKey: ["monitor", t, "GC"],
      queryFn: () => apiGet<{ rows: MonitorRow[] }>(`/api/monitor?type=${t}&subsidiary=GC`),
      enabled,
    })),
  });
  const rows = qs.flatMap((q) => q.data?.rows ?? []).filter((r) => r.suggested_po > 0);
  rows.sort((a, b) => (a.days_of_stock ?? 1e9) - (b.days_of_stock ?? 1e9));
  return { rows, loading: enabled && qs.some((q) => q.isLoading), error: qs.find((q) => q.error)?.error as Error | undefined };
}

const lasts = (r: MonitorRow, today: string) => {
  const d = r.days_of_stock;
  if (d == null || !Number.isFinite(d)) return { t: "No use", s: "" };
  if (d < 1) return { t: "Empty now", s: "" };
  if (d > 180) return { t: "6+ months", s: "" };
  return { t: shortDate(addDays(today, Math.floor(d))), s: `in ${Math.floor(d)} days` };
};

export function GoldCrestTable({ gc }: { gc: ReturnType<typeof useGoldCrest> }) {
  const [filter, setFilter] = useState("");
  const today = todayIso();
  const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
  const rows = gc.rows.filter((r) => words.every((w) => `${r.itemid} ${partName(r.itemid)}`.toLowerCase().includes(w)));
  if (gc.error) return <div className="u-error">Couldn't load Gold Crest: {gc.error.message}</div>;
  if (!gc.rows.length) return <div className="tablewrap"><div className="u-empty">Nothing to order for Gold Crest today.</div></div>;
  const unit = (r: MonitorRow) => (isYards(r.item_type) ? "yd" : "units");
  const exportRows = () =>
    downloadCsv(`to-order-gold-crest-${today}.csv`, rows.map((r) => ({
      Part: r.itemid,
      Name: partName(r.itemid),
      Category: CATEGORY_LABEL[r.item_type] ?? r.item_type,
      Stock: Math.round(r.qoh.total),
      "Used, last 30 days": Math.round(r.burn_30d),
      "On order": Math.round(r.inbound.total),
      "Runs out": lasts(r, today).t,
      "Need to order (60 days of use)": Math.round(r.suggested_po),
      Unit: unit(r),
    })));
  return (
    <>
      <ListBar title={`Gold Crest · ${rows.length}`} search={filter} onSearch={setFilter}>
        <ExportButton onClick={exportRows} />
      </ListBar>
      <div className="tablewrap">
        <table className="dt left">
          <thead>
            <tr>
              <th>Part</th>
              <th>Stock</th>
              <th className="hide-m">Used<br /><span className="sub">last 30 days</span></th>
              <th>On order</th>
              <th className="l">Runs out<br /><span className="sub">at the last 30 days' pace</span></th>
              <th>Need to order<br /><span className="sub">60 days of use</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const l = lasts(r, today);
              return (
                <tr key={`${r.item_type}-${r.itemid}`}>
                  <td className="l">
                    <span className="code">{r.itemid}</span>
                    <span className="name">{partName(r.itemid) || CATEGORY_LABEL[r.item_type]}</span>
                  </td>
                  <td>{fmt(r.qoh.total)} <span className="sub">{unit(r)}</span></td>
                  <td className="hide-m">{fmt(r.burn_30d)}</td>
                  <td>{r.inbound.total > 0 ? fmt(r.inbound.total) : <span className="muted">Nothing</span>}</td>
                  <td className="l">
                    <span className="ro">
                      <b className={l.t === "Empty now" || (r.days_of_stock ?? 1e9) <= 30 ? "late-t" : ""}>{l.t}</b>
                      {l.s && <span className="ro-s">{l.s}</span>}
                    </span>
                  </td>
                  <td><b>{fmt(r.suggested_po)}</b></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="fine">
        Gold Crest (Texas) is a separate company with no forecast, so its Need to order = 60 days of use at the last 30
        days' pace, minus stock and open orders. Runs out soonest first. Nothing here changes NetSuite.
      </p>
    </>
  );
}
